import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Switch } from '@material-ui/core'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Tooltip from '@material-ui/core/Tooltip'
import Fade from '@material-ui/core/Fade'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles } from '@material-ui/core/styles'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useAuth } from '../../../Contexts/AuthProvider'
import useTranslation from '../../../Hooks/useTranslation'
import SearchInput from '../../../Components/SearchInput'
import TablePagination from '../../PredictiveMaintenance/Equipment/TablePagination'
import DmsService from '../../../Services/DMS/dms-api.service'
import AlertToastr from '../../../utils/alert'
import RetentionPeriodConfirmModal from './RetentionPeriodConfirmModal'

const ascConst = 'ascending'
const descConst = 'descending'
const arrowDownClassName = 'hasArrow'
const arrowUpClassName = 'hasArrow up'

const SORT_KEY_TO_API_ALL_UNITS = {
    uId: 'unitUid',
    name: 'unitName',
    changedBy: 'changedBy',
    lastChanged: 'lastChanged',
    addedOn: 'addedOn',
    status: 'isEnabled',
}

const SORT_KEY_TO_API_ACTIVITY = {
    uId: 'UnitUId',
    name: 'UnitName',
    totalEnabledTime: 'TotalEnabledTime',
}

const NEXT_TIER_MODE = 'nextTier'
const ALL_TIERS_MODE = 'allTiers'
const TABLE_VIEW_ALL_UNITS = 'allUnits'
const TABLE_VIEW_ACTIVITY = 'activity'
const ZERO_UUID = '00000000-0000-0000-0000-000000000000'
const ACTIVITY_DEFAULT_SORT = { key: 'totalEnabledTime', direction: descConst }
const SUMMARY_REFRESH_AFTER_TOGGLE_MS = 600
const SCALE_LABEL_MILESTONES = [5, 10, 20, 50, 100]
const SCALE_LABEL_MILESTONE_SET = new Set(SCALE_LABEL_MILESTONES)

// MUI v4 Tooltip has no followCursor - anchor the popper to a virtual point at the pointer
const CursorTooltip = ({ title, children, ...props }) => {
    const pointRef = useRef({ x: 0, y: 0 })
    const popperRef = useRef(null)
    const anchorEl = useMemo(() => ({
        clientWidth: 0,
        clientHeight: 0,
        getBoundingClientRect: () => {
            const { x, y } = pointRef.current
            return { top: y, bottom: y, left: x, right: x, width: 0, height: 0 }
        },
    }), [])

    const handleMouseMove = (e) => {
        pointRef.current = { x: e.clientX, y: e.clientY }
        popperRef.current?.update()
    }

    return (
        <Tooltip
            {...props}
            title={title}
            onMouseMove={handleMouseMove}
            PopperProps={{ anchorEl, popperRef }}
        >
            {children}
        </Tooltip>
    )
}

function getScaleLabelThresholds({
    scaleMax,
    currentPackageAllowedDevices,
    extraThresholds = [],
}) {
    const labels = new Set()

    SCALE_LABEL_MILESTONES.forEach((milestone) => {
        if (milestone <= scaleMax) labels.add(milestone)
    })

    const currentPackage = Number(currentPackageAllowedDevices) || 0
    if (
        currentPackage > 0 &&
        currentPackage <= scaleMax &&
        !SCALE_LABEL_MILESTONE_SET.has(currentPackage)
    ) {
        labels.add(currentPackage)
    }

    extraThresholds.forEach((threshold) => {
        if (threshold > 0 && threshold <= scaleMax) {
            labels.add(threshold)
        }
    })

    return [...labels].sort((a, b) => a - b)
}

function buildScaleLabelItems(thresholds, subscriptionPackages, scaleMax) {
    return thresholds.map((allowedDevices, index, arr) => {
        const pkg = subscriptionPackages.find(
            (item) => item.allowedDevices === allowedDevices,
        )
        return {
            allowedDevices,
            name: pkg?.name ?? `Cloud ${allowedDevices}`,
            key: `scale-label-${allowedDevices}`,
            isFirst: index === 0,
            isLast: index === arr.length - 1,
            leftPercent: scaleMax > 0 ? (allowedDevices / scaleMax) * 100 : 0,
        }
    })
}

function renderTierProgressTrack(trackItems) {
    return (
        <div className="subscriptionTierProgressTrack">
            <div className="subscriptionTierProgress">
                {trackItems.map((item) => (
                    <span key={item.key} className={item.className} />
                ))}
            </div>
        </div>
    )
}

const WORD_TO_NUMBER = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    fifteen: 15,
    thirty: 30,
    sixty: 60,
    ninety: 90,
}

const formatDateTime = (value) => {
    if (!value) return '-'
    const dt = parseDateAsUtc(value)
    if (!dt) return '-'
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(dt)
}

const formatDateTimeLong = (value) => {
    if (!value) return '-'
    const dt = parseDateAsUtc(value)
    if (!dt) return '-'
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(dt)
}

const parseDateAsUtc = (value) => {
    if (!value) return null
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value
    }
    if (typeof value === 'number') {
        const dt = new Date(value)
        return Number.isNaN(dt.getTime()) ? null : dt
    }
    const raw = String(value).trim()
    if (!raw) return null

    // Backend may return ISO-like strings without timezone.
    // Treat such values as UTC to avoid local timezone/DST shifts.
    const hasTimezoneSuffix = /(z|[+-]\d{2}:\d{2})$/i.test(raw)
    const normalized = hasTimezoneSuffix ? raw : `${raw}Z`
    const parsed = new Date(normalized)
    if (!Number.isNaN(parsed.getTime())) return parsed

    const fallback = new Date(raw)
    return Number.isNaN(fallback.getTime()) ? null : fallback
}

const getApiErrorMessageFromResponseData = (data) => {
    if (data == null) return null
    if (typeof data === 'string') {
        const t = data.trim()
        return t || null
    }
    if (typeof data === 'object') {
        const direct =
            data.Message ??
            data.message ??
            data.error ??
            data.Error ??
            data.detail ??
            data.Detail
        if (direct != null && String(direct).trim() !== '') {
            return String(direct).trim()
        }
        if (data.title && data.detail) {
            return String(data.detail).trim()
        }
        if (data.title) {
            return String(data.title).trim()
        }
        if (data.errors && typeof data.errors === 'object') {
            const parts = Object.values(data.errors)
                .flat()
                .map((v) => (Array.isArray(v) ? v[0] : v))
                .filter((v) => v != null && String(v).trim() !== '')
            if (parts.length > 0) return String(parts[0])
        }
    }
    return null
}

const getApiErrorMessage = (error) => {
    if (!error) return null
    const fromResponse = getApiErrorMessageFromResponseData(error?.response?.data)
    if (fromResponse) return fromResponse
    return error.message || null
}

/** ISO timestamps for PATCH usage interval (UTC). Missing end uses current time (open-ended interval). */
const intervalBoundaryToIsoForApi = (raw) => {
    if (raw == null || raw === '') return new Date().toISOString()
    const d = parseDateAsUtc(raw)
    if (!d || Number.isNaN(d.getTime())) return new Date().toISOString()
    return d.toISOString()
}

const buildUsageIntervalPatchBody = (row, marker, excludeFromBilling) => {
    const startRaw = marker.tooltipData.start
    const endRaw = marker.tooltipData.end
    const intervalStart = intervalBoundaryToIsoForApi(startRaw)
    const endNonEmpty = endRaw != null && String(endRaw).trim() !== ''
    const shouldSendIntervalEnd = marker.isCurrent !== true && endNonEmpty
    const body = {
        unitUId: row.unitUId,
        intervalStart,
        excludeFromBilling: excludeFromBilling,
    }
    if (shouldSendIntervalEnd) {
        body.intervalEnd = intervalBoundaryToIsoForApi(endRaw)
    }
    return body
}

const formatDurationFromRange = (startValue, endValue) => {
    const startDate = parseDateAsUtc(startValue)
    if (!startDate) return '-'

    const endDate = endValue ? parseDateAsUtc(endValue) : new Date()
    if (!endDate) return '-'

    const durationMs = Math.max(0, endDate.getTime() - startDate.getTime())
    const totalSeconds = Math.floor(durationMs / 1000)
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    return `${days}d ${hours}h:${minutes}m:${seconds}s`
}

const isActivationAction = (actionType) => {
    const value = String(actionType || '').toLowerCase()
    return value === 'added' || value === 'enabled' || value === 'activated'
}

const isDeactivationAction = (actionType) => {
    const value = String(actionType || '').toLowerCase()
    return value === 'removed' || value === 'disabled' || value === 'deactivated'
}

const formatActor = (value) => {
    const normalized = String(value || '').trim()
    if (!normalized || normalized === '-') return '-'
    if (normalized === ZERO_UUID) return 'System'
    return normalized
}

const isUuidLike = (value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        String(value || '').trim(),
    )

const resolveActorName = (value, usersById) => {
    const normalized = String(value || '').trim()
    if (!normalized || normalized === '-') return '-'
    if (normalized === ZERO_UUID) return 'System'
    const user = usersById[normalized]
    if (!user) return isUuidLike(normalized) ? 'System Admin' : normalized
    return user.name || user.fullName || user.email || normalized
}

const buildActivityMarkersFromIntervals = (item, activityTimeAxis, usersById) => {
    const intervals = Array.isArray(item.intervals) ? item.intervals : []
    const axisStartMs = activityTimeAxis.start.getTime()
    const axisEndMs = activityTimeAxis.end.getTime()
    const axisRange = Math.max(1, axisEndMs - axisStartMs)

    return intervals
        .map((interval, idx) => {
            const fromRaw = interval.from ?? interval.From
            const toRaw = interval.to ?? interval.To ?? null
            const startDate = parseDateAsUtc(fromRaw)
            if (!startDate) return null
            const endDate = toRaw ? parseDateAsUtc(toRaw) : null
            const periodStartMs = startDate.getTime()
            const hasClose = Boolean(endDate)
            const periodEndMs = hasClose ? endDate.getTime() : axisEndMs
            if (periodEndMs <= periodStartMs) return null
            const visStartMs = Math.max(periodStartMs, axisStartMs)
            const visEndMs = Math.min(periodEndMs, axisEndMs)
            if (visEndMs <= visStartMs) return null
            const left = ((visStartMs - axisStartMs) / axisRange) * 100
            const width = ((visEndMs - visStartMs) / axisRange) * 100
            if (width <= 0) return null
            const excludeFromBilling = Boolean(
                interval.excludeFromBilling ?? interval.ExcludeFromBilling,
            )
            const durationStr =
                interval.duration ??
                interval.Duration ??
                formatDurationFromRange(fromRaw, hasClose ? toRaw : null)
            const nextInterval = intervals[idx + 1]
            const activatedBy =
                interval.actionBy ??
                interval.ActionBy ??
                interval.activatedByUserId ??
                interval.ActivatedByUserId ??
                interval.activatedBy ??
                interval.ActivatedBy ??
                '-'
            const deactivatedByRaw =
                interval.deactivatedByUserId ??
                interval.DeactivatedByUserId ??
                interval.deactivatedBy ??
                interval.DeactivatedBy ??
                (hasClose && nextInterval
                    ? nextInterval.actionBy ?? nextInterval.ActionBy
                    : null) ??
                '-'
            const activatedByDisplay =
                interval.activatedByDisplay ??
                interval.ActivatedByDisplay ??
                interval.activatedByName ??
                interval.ActivatedByName ??
                resolveActorName(activatedBy, usersById)
            const deactivatedByDisplay =
                interval.deactivatedByDisplay ??
                interval.DeactivatedByDisplay ??
                interval.deactivatedByName ??
                interval.DeactivatedByName ??
                resolveActorName(deactivatedByRaw, usersById)
            return {
                left,
                width,
                isCurrent: !hasClose,
                excludeFromBilling,
                key: `interval-${String(fromRaw)}-${idx}`,
                tooltipData: {
                    variant: 'interval',
                    start: fromRaw,
                    end: toRaw,
                    duration: durationStr,
                    excludeFromBilling,
                    activatedBy,
                    deactivatedBy: deactivatedByRaw,
                    activatedByDisplay,
                    deactivatedByDisplay,
                },
            }
        })
        .filter(Boolean)
}

const buildActivityMarkersFromLogs = (item, activityTimeAxis, usersById) => {
    const logs = Array.isArray(item.logs) ? item.logs : []
    const normalizedLogs = [...logs].sort(
        (a, b) =>
            (parseDateAsUtc(a.timestamp)?.getTime() ?? 0) -
            (parseDateAsUtc(b.timestamp)?.getTime() ?? 0),
    )
    const latestLog =
        normalizedLogs.length > 0
            ? normalizedLogs[normalizedLogs.length - 1]
            : null
    const axisStartMs = activityTimeAxis.start.getTime()
    const axisEndMs = activityTimeAxis.end.getTime()
    const axisRange = Math.max(1, axisEndMs - axisStartMs)

    const activityMarkers = normalizedLogs
        .map((log, idx) => {
            if (!isActivationAction(log.actionType)) return null
            const dt = parseDateAsUtc(log.timestamp)
            if (!dt) return null
            const deactivationLog = normalizedLogs
                .slice(idx + 1)
                .find((entry) => isDeactivationAction(entry.actionType))
            const deactivationDt = deactivationLog
                ? parseDateAsUtc(deactivationLog.timestamp)
                : null
            const periodStartMs = dt.getTime()
            const hasClose = Boolean(deactivationDt)
            const periodEndMs = hasClose ? deactivationDt.getTime() : axisEndMs
            if (periodEndMs <= periodStartMs) return null
            const visStartMs = Math.max(periodStartMs, axisStartMs)
            const visEndMs = Math.min(periodEndMs, axisEndMs)
            if (visEndMs <= visStartMs) return null
            const left = ((visStartMs - axisStartMs) / axisRange) * 100
            const width = ((visEndMs - visStartMs) / axisRange) * 100
            if (width <= 0) return null
            const start = log.timestamp
            const end = hasClose ? deactivationLog?.timestamp : null
            const isCurrent = !hasClose
            const activatedBy = log.actionBy || '-'
            const deactivatedBy = deactivationLog?.actionBy || '-'
            return {
                left,
                width,
                isCurrent,
                excludeFromBilling: false,
                key: `${log.timestamp}-${idx}`,
                tooltipData: {
                    variant: 'logs',
                    start,
                    end,
                    duration: formatDurationFromRange(start, end),
                    activatedBy,
                    deactivatedBy,
                    activatedByDisplay: resolveActorName(activatedBy, usersById),
                    deactivatedByDisplay: resolveActorName(deactivatedBy, usersById),
                },
            }
        })
        .filter(Boolean)

    return { activityMarkers, latestLog }
}

const getTokenNumber = (token) => {
    if (!token) return null
    if (/^\d+$/.test(token)) return Number(token)
    return WORD_TO_NUMBER[token] ?? null
}

const resolveRetentionPeriodMonths = (value) => {
    if (!value) return 12
    const raw = String(value)
    const normalized = raw.toLowerCase()
    const compact = normalized.replace(/[\s_-]/g, '')

    if (compact.includes('threemonth')) return 3
    if (compact.includes('sixmonth')) return 6
    if (compact.includes('oneyear')) return 12
    if (compact.includes('twoyear')) return 24
    if (compact.includes('threeyear')) return 36
    if (compact.includes('fiveyear')) return 60

    const yearMatch = compact.match(/(\d+)(year|yr|y)/)
    if (yearMatch) return Number(yearMatch[1]) * 12
    const monthMatch = compact.match(/(\d+)(month|mo|m)/)
    if (monthMatch) return Number(monthMatch[1])
    const dayMatch = compact.match(/(\d+)(day|d)/)
    if (dayMatch) return Math.max(1, Math.ceil(Number(dayMatch[1]) / 30))

    const words = normalized.match(/[a-z]+/g) || []
    const yearWordIndex = words.findIndex((w) => w === 'year' || w === 'years')
    if (yearWordIndex > 0) {
        const n = getTokenNumber(words[yearWordIndex - 1])
        if (n != null) return n * 12
    }
    const monthWordIndex = words.findIndex((w) => w === 'month' || w === 'months')
    if (monthWordIndex > 0) {
        const n = getTokenNumber(words[monthWordIndex - 1])
        if (n != null) return n
    }
    const dayWordIndex = words.findIndex((w) => w === 'day' || w === 'days')
    if (dayWordIndex > 0) {
        const n = getTokenNumber(words[dayWordIndex - 1])
        if (n != null) return Math.max(1, Math.ceil(n / 30))
    }

    return 12
}

const buildMonthDayTicks = (monthDate) => {
    if (!(monthDate instanceof Date) || Number.isNaN(monthDate.getTime())) {
        return [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31]
    }
    const daysInMonth = new Date(
        monthDate.getFullYear(),
        monthDate.getMonth() + 1,
        0,
    ).getDate()
    const tickCount = 11
    const ticks = new Set()
    for (let idx = 0; idx < tickCount; idx += 1) {
        const day = Math.round(1 + ((daysInMonth - 1) * idx) / (tickCount - 1))
        ticks.add(Math.min(daysInMonth, Math.max(1, day)))
    }
    return Array.from(ticks).sort((a, b) => a - b)
}

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
}))

const UnitSettings = () => {
    const translation = useTranslation()
    const classes = useStyles()
    const queryClient = useQueryClient()
    const { userLoaded, currentUser } = useAuth()
    const currentCompany = useSelector(state => state.dashboard.currentCompany)

    const [searchFilter, setSearchFilter] = useState()
    const [sortConfig, setSortConfig] = useState({ key: 'uId', direction: ascConst })
    const [pageNumber, setPageNumber] = useState(1)
    const [pageSize, setPageSize] = useState(15)
    const [togglingUnitUid, setTogglingUnitUid] = useState(null)
    const [subscriptionMode, setSubscriptionMode] = useState(NEXT_TIER_MODE)
    const [tableView, setTableView] = useState(TABLE_VIEW_ACTIVITY)
    const [selectedSummaryRetentionPeriod, setSelectedSummaryRetentionPeriod] = useState(null)
    const [selectedActivityRetentionPeriod, setSelectedActivityRetentionPeriod] = useState(null)
    const [packageUpgradeModalOpen, setPackageUpgradeModalOpen] = useState(false)
    const [packageUpgradeItem, setPackageUpgradeItem] = useState(null)
    const [activeActivityMarkerTooltipId, setActiveActivityMarkerTooltipId] = useState(null)
    const retentionPeriodBeforePendingChangeRef = useRef(null)
    const pendingRetentionPeriodRef = useRef(null)
    const subscriptionsSummaryInvalidateTimeoutRef = useRef(null)
    const unitSettingsSortRef = useRef({ key: 'uId', direction: ascConst })

    useEffect(() => {
        return () => {
            if (subscriptionsSummaryInvalidateTimeoutRef.current != null) {
                clearTimeout(subscriptionsSummaryInvalidateTimeoutRef.current)
            }
        }
    }, [])

    const getRetentionPeriodValue = useCallback((payload) => {
        if (!payload || typeof payload !== 'object') return null
        const value = payload.dataRetentionPeriod ?? payload.DataRetentionPeriod
        return value == null ? null : value
    }, [])

    const isSuperUser = useMemo(() => currentUser?.isSuperuser?.() ?? false, [currentUser])

    const companyConfig = useMemo(() => {
        if (!isSuperUser) {
            return {}
        }
        return {
            headers: {
                CompanyId: currentCompany?.id,
            },
        }
    }, [isSuperUser, currentCompany?.id])

    const queryEnabled =
        userLoaded &&
        currentUser &&
        (!isSuperUser || Boolean(currentCompany?.id))

    const subscriptionsRetentionOptionsQuery = useQuery(
        ['subscriptionsRetentionOptions', currentCompany?.id],
        () => DmsService.getSubscriptionsDataRetentionPeriodOptions(companyConfig),
        {
            enabled: queryEnabled,
            keepPreviousData: true,
        },
    )

    const subscriptionsRetentionPeriodQuery = useQuery(
        ['subscriptionsRetentionPeriod', currentCompany?.id],
        () => DmsService.getSubscriptionsDataRetentionPeriod(companyConfig),
        {
            enabled: queryEnabled,
            keepPreviousData: true,
        },
    )

    const retentionPeriodFromSettingsEndpoint = useMemo(
        () => getRetentionPeriodValue(subscriptionsRetentionPeriodQuery.data?.data),
        [subscriptionsRetentionPeriodQuery.data, getRetentionPeriodValue],
    )

    const summaryRequestDataRetentionPeriod = useMemo(
        () =>
            selectedSummaryRetentionPeriod != null
                ? selectedSummaryRetentionPeriod
                : retentionPeriodFromSettingsEndpoint,
        [selectedSummaryRetentionPeriod, retentionPeriodFromSettingsEndpoint],
    )

    const summaryQueryEnabled =
        queryEnabled &&
        summaryRequestDataRetentionPeriod != null &&
        String(summaryRequestDataRetentionPeriod).trim() !== ''

    const subscriptionsSummaryQuery = useQuery(
        ['subscriptionsSummary', currentCompany?.id, summaryRequestDataRetentionPeriod],
        () => DmsService.getSubscriptionsSummary(summaryRequestDataRetentionPeriod, companyConfig),
        {
            enabled: summaryQueryEnabled,
            keepPreviousData: true,
            onError: (error) => {
                const msg =
                    error?.response?.data?.Message ||
                    error?.response?.data?.message ||
                    error?.message
                if (msg) {
                    AlertToastr.showErrorAlert(msg)
                }
            },
        },
    )
    const usersShortQuery = useQuery(
        ['unitSettingsUsersShort', currentCompany?.id],
        () => DmsService.getUsersShort(companyConfig),
        {
            enabled: queryEnabled,
            keepPreviousData: true,
        },
    )
    const dataRetentionPeriodFromApi = useMemo(() => {
        const retentionResponse = subscriptionsRetentionPeriodQuery.data?.data
        const summaryRetention = subscriptionsSummaryQuery.data?.data?.retentionPeriod
        return getRetentionPeriodValue(retentionResponse) ?? getRetentionPeriodValue(summaryRetention)
    }, [subscriptionsRetentionPeriodQuery.data, subscriptionsSummaryQuery.data, getRetentionPeriodValue])

    useEffect(() => {
        if (dataRetentionPeriodFromApi == null) return
        setSelectedSummaryRetentionPeriod((prev) =>
            prev == null ? dataRetentionPeriodFromApi : prev,
        )
        setSelectedActivityRetentionPeriod((prev) =>
            prev == null ? dataRetentionPeriodFromApi : prev,
        )
    }, [dataRetentionPeriodFromApi])

    const summaryDataRetentionPeriodParam =
        selectedSummaryRetentionPeriod != null
            ? selectedSummaryRetentionPeriod
            : dataRetentionPeriodFromApi
    const activityDataRetentionPeriodParam =
        selectedActivityRetentionPeriod != null
            ? selectedActivityRetentionPeriod
            : dataRetentionPeriodFromApi

    const sortByApi = useMemo(() => {
        if (tableView === TABLE_VIEW_ACTIVITY) {
            return SORT_KEY_TO_API_ACTIVITY[sortConfig.key] || 'UnitUId'
        }
        return SORT_KEY_TO_API_ALL_UNITS[sortConfig.key] || 'unitUid'
    }, [tableView, sortConfig.key])
    const sortDescending = sortConfig.direction === descConst

    const allUnitsQuery = useQuery(
        [
            'unitSettings',
            currentCompany?.id,
            pageNumber,
            pageSize,
            sortByApi,
            sortDescending,
            searchFilter,
        ],
        () =>
            DmsService.getUnitSettings(
                {
                    pageNumber,
                    pageSize,
                    sortBy: sortByApi,
                    sortDescending,
                    searchTerm: searchFilter,
                },
                companyConfig,
            ),
        {
            enabled: queryEnabled && tableView === TABLE_VIEW_ALL_UNITS,
            keepPreviousData: true,
            onError: (error) => {
                const msg =
                    error?.response?.data?.Message ||
                    error?.response?.data?.message ||
                    error?.message
                if (msg) {
                    AlertToastr.showErrorAlert(msg)
                }
            },
        },
    )

    const activityQuery = useQuery(
        [
            'unitSettingsUsage',
            currentCompany?.id,
            pageNumber,
            pageSize,
            sortByApi,
            sortDescending,
            searchFilter,
            activityDataRetentionPeriodParam,
        ],
        () =>
            DmsService.getUnitSettingsLogsUsage(
                {
                    pageNumber,
                    pageSize,
                    sortBy: sortByApi,
                    sortDescending,
                    searchTerm: searchFilter,
                    dataRetentionPeriod: activityDataRetentionPeriodParam,
                },
                companyConfig,
            ),
        {
            enabled: queryEnabled && tableView === TABLE_VIEW_ACTIVITY,
            keepPreviousData: true,
            onError: (error) => {
                const msg =
                    error?.response?.data?.Message ||
                    error?.response?.data?.message ||
                    error?.message
                if (msg) {
                    AlertToastr.showErrorAlert(msg)
                }
            },
        },
    )

    const usersById = useMemo(() => {
        const raw = usersShortQuery.data?.data
        if (!Array.isArray(raw)) return {}
        return raw.reduce((acc, user) => {
            const id = user?.id
            if (id) acc[id] = user
            return acc
        }, {})
    }, [usersShortQuery.data])

    const activityRetentionPeriodValue =
        activityDataRetentionPeriodParam ||
        activityQuery.data?.data?.dataRetentionPeriod ||
        dataRetentionPeriodFromApi

    const activityBillableUnitsCount = useMemo(() => {
        const payload = activityQuery.data?.data
        if (!payload) return undefined
        const raw = payload.billableUnitsCount ?? payload.BillableUnitsCount
        if (raw == null || raw === '') return undefined
        const n = Number(raw)
        return Number.isFinite(n) ? n : undefined
    }, [activityQuery.data])

    const activityTimeAxis = useMemo(() => {
        const months = Math.max(1, resolveRetentionPeriodMonths(activityRetentionPeriodValue))
        const now = new Date()
        const end = new Date(now)
        let start
        let labels = []

        if (months > 12) {
            const years = Math.max(1, Math.ceil(months / 12))
            const currentYear = now.getFullYear()
            const firstYear = currentYear - years + 1
            start = new Date(firstYear, 0, 1, 0, 0, 0, 0)
            labels = new Array(years).fill(null).map((_, idx) => ({
                key: `year-${firstYear + idx}`,
                label: String(firstYear + idx),
            }))
        } else {
            start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0)
            start.setMonth(start.getMonth() - (months - 1))
            labels = new Array(months).fill(null).map((_, idx) => {
                const d = new Date(start)
                d.setMonth(start.getMonth() + idx)
                return {
                    key: `month-${d.getFullYear()}-${d.getMonth()}`,
                    label: new Intl.DateTimeFormat('en-US', { month: 'short' }).format(d),
                    monthDate: d,
                }
            })
        }

        return {
            start,
            end,
            labels,
        }
    }, [activityRetentionPeriodValue])
    const shouldShowDetailedActivityScale = useMemo(() => {
        const months = Math.max(1, resolveRetentionPeriodMonths(activityRetentionPeriodValue))
        return months === 3 || months === 6
    }, [activityRetentionPeriodValue])

    const items = useMemo(
        () => {
            const rawItems =
                tableView === TABLE_VIEW_ACTIVITY
                    ? activityQuery.data?.data?.items
                    : allUnitsQuery.data?.data?.items
            if (!Array.isArray(rawItems)) return []
            if (tableView !== TABLE_VIEW_ACTIVITY) {
                return rawItems.map((item) => ({
                    unitUId: item.unitUid ?? item.unitUId,
                    unitName: item.unitName ?? '-',
                    changedBy: resolveActorName(
                        item.lastChangedByUserId || item.changedBy || '-',
                        usersById,
                    ),
                    lastChanged: formatDateTime(item.lastChanged),
                    addedOn: formatDateTime(item.addedOn),
                    isEnabled: !!item.isEnabled,
                    isDeleted: !!item.isDeleted,
                }))
            }
            return rawItems.map((item) => {
                const totalEnabledTimeFromApi =
                    item.totalEnabledTime ?? item.TotalEnabledTime ?? '-'
                const unitNameFromApi = item.unitName ?? item.UnitName ?? null
                const intervalList = Array.isArray(item.intervals) ? item.intervals : []
                let activityMarkers
                let latestLog = null
                if (intervalList.length > 0) {
                    activityMarkers = buildActivityMarkersFromIntervals(
                        item,
                        activityTimeAxis,
                        usersById,
                    )
                } else {
                    const built = buildActivityMarkersFromLogs(
                        item,
                        activityTimeAxis,
                        usersById,
                    )
                    activityMarkers = built.activityMarkers
                    latestLog = built.latestLog
                }
                return {
                    unitUId: item.unitUId ?? item.UnitUId,
                    unitName: unitNameFromApi || latestLog?.unitName || '-',
                    totalEnabledTime: totalEnabledTimeFromApi,
                    isBillable: (item.isBillable ?? item.IsBillable) !== false,
                    isDeleted: !!(item.isDeleted ?? item.IsDeleted),
                    activityMarkers,
                }
            })
        },
        [tableView, activityQuery.data, allUnitsQuery.data, activityTimeAxis, usersById],
    )
    const totalCount =
        tableView === TABLE_VIEW_ACTIVITY
            ? activityQuery.data?.data?.totalCount ?? 0
            : allUnitsQuery.data?.data?.totalCount ?? 0

    const pageCount = useMemo(() => {
        if (totalCount <= 0) {
            return 0
        }
        return Math.max(1, Math.ceil(totalCount / pageSize))
    }, [totalCount, pageSize])

    const pagination = useMemo(
        () => ({
            pageNumber,
            pageSize,
            itemsCount: items.length,
            itemsTotalCount: totalCount,
            pageCount: totalCount === 0 ? 0 : pageCount,
        }),
        [pageNumber, pageSize, items.length, totalCount, pageCount],
    )

    useEffect(() => {
        setPageNumber(1)
        setActiveActivityMarkerTooltipId(null)
    }, [searchFilter])

    useEffect(() => {
        if (tableView !== TABLE_VIEW_ACTIVITY) {
            setActiveActivityMarkerTooltipId(null)
        }
    }, [tableView])

    useEffect(() => {
        if (!activeActivityMarkerTooltipId) return undefined
        const handleMouseDown = (event) => {
            const target = event.target
            if (target.closest?.('.MuiTooltip-popper')) return
            if (target.closest?.('.activityUsageTooltip')) return
            setActiveActivityMarkerTooltipId(null)
        }
        document.addEventListener('mousedown', handleMouseDown)
        return () => document.removeEventListener('mousedown', handleMouseDown)
    }, [activeActivityMarkerTooltipId])

    const patchMutation = useMutation(
        ({ unitUid, isEnabled }) => DmsService.patchUnitSettings({ unitUid, isEnabled }, companyConfig),
        {
            onSuccess: (axiosResponse) => {
                const body = axiosResponse?.data ?? axiosResponse
                const item = body?.item ?? body?.Item
                const code = item?.confirmationCode ?? item?.ConfirmationCode
                if (code) {
                    setPackageUpgradeItem(item)
                    setPackageUpgradeModalOpen(true)
                    return
                }
                const failed =
                    body?.isSuccessful === false ||
                    body?.IsSuccessful === false ||
                    body?.success === false ||
                    body?.Success === false
                if (failed) {
                    const msg =
                        getApiErrorMessageFromResponseData(body) || 'Request failed'
                    AlertToastr.showErrorAlert(msg)
                    return
                }
                queryClient.invalidateQueries(['unitSettings'])
                if (subscriptionsSummaryInvalidateTimeoutRef.current != null) {
                    clearTimeout(subscriptionsSummaryInvalidateTimeoutRef.current)
                }
                subscriptionsSummaryInvalidateTimeoutRef.current = setTimeout(() => {
                    subscriptionsSummaryInvalidateTimeoutRef.current = null
                    queryClient.invalidateQueries(['subscriptionsSummary'])
                }, SUMMARY_REFRESH_AFTER_TOGGLE_MS)
            },
            onError: (error) => {
                const msg = getApiErrorMessage(error)
                if (msg) {
                    AlertToastr.showErrorAlert(msg)
                }
            },
            onSettled: () => {
                setTogglingUnitUid(null)
            },
        },
    )

    const patchUsageIntervalMutation = useMutation(
        ({ body, config }) => DmsService.patchUnitSettingsLogsUsageInterval(body, config),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['unitSettingsUsage'])
                queryClient.invalidateQueries(['subscriptionsSummary'])
            },
            onError: (error) => {
                const msg =
                    error?.response?.data?.Message ||
                    error?.response?.data?.message ||
                    error?.message
                if (msg) {
                    AlertToastr.showErrorAlert(msg)
                }
            },
        },
    )

    useEffect(() => {
        if (pageCount > 0 && pageNumber > pageCount) {
            setPageNumber(pageCount)
        }
    }, [pageCount, pageNumber])

    const patchRetentionPeriodMutation = useMutation(
        ({ dataRetentionPeriod }) =>
            DmsService.patchSubscriptionsDataRetentionPeriod({ dataRetentionPeriod }, companyConfig),
        {
            onSuccess: (axiosResponse, variables) => {
                const body = axiosResponse?.data ?? axiosResponse
                const item = body?.item ?? body?.Item
                const code = item?.confirmationCode ?? item?.ConfirmationCode
                const newPeriod = variables?.dataRetentionPeriod
                if (code) {
                    pendingRetentionPeriodRef.current = newPeriod
                    setPackageUpgradeItem(item)
                    setPackageUpgradeModalOpen(true)
                    return
                }
                pendingRetentionPeriodRef.current = null
                retentionPeriodBeforePendingChangeRef.current = null
                if (newPeriod != null) {
                    setSelectedSummaryRetentionPeriod(newPeriod)
                    setSelectedActivityRetentionPeriod(newPeriod)
                }
                queryClient.invalidateQueries(['subscriptionsSummary'])
                queryClient.invalidateQueries(['subscriptionsRetentionPeriod'])
                if (
                    newPeriod != null &&
                    queryEnabled &&
                    tableView !== TABLE_VIEW_ACTIVITY
                ) {
                    const sortBy = SORT_KEY_TO_API_ACTIVITY[ACTIVITY_DEFAULT_SORT.key] || 'UnitUId'
                    const sortDescending = ACTIVITY_DEFAULT_SORT.direction === descConst
                    queryClient.prefetchQuery(
                        [
                            'unitSettingsUsage',
                            currentCompany?.id,
                            1,
                            15,
                            sortBy,
                            sortDescending,
                            undefined,
                            newPeriod,
                        ],
                        () =>
                            DmsService.getUnitSettingsLogsUsage(
                                {
                                    pageNumber: 1,
                                    pageSize: 15,
                                    sortBy,
                                    sortDescending,
                                    searchTerm: undefined,
                                    dataRetentionPeriod: newPeriod,
                                },
                                companyConfig,
                            ),
                    )
                }
            },
            onError: (error) => {
                pendingRetentionPeriodRef.current = null
                const prev = retentionPeriodBeforePendingChangeRef.current
                if (prev != null) {
                    setSelectedSummaryRetentionPeriod(prev)
                    retentionPeriodBeforePendingChangeRef.current = null
                }
                const msg =
                    error?.response?.data?.Message ||
                    error?.response?.data?.message ||
                    error?.message
                if (msg) {
                    AlertToastr.showErrorAlert(msg)
                }
            },
        },
    )

    const confirmPackageUpgradeMutation = useMutation(
        ({ code }) =>
            DmsService.postSubscriptionsPackageUpgradeConfirm({ code }, companyConfig),
        {
            onSuccess: () => {
                const newPeriod = pendingRetentionPeriodRef.current
                pendingRetentionPeriodRef.current = null
                retentionPeriodBeforePendingChangeRef.current = null
                if (newPeriod != null) {
                    setSelectedSummaryRetentionPeriod(newPeriod)
                    setSelectedActivityRetentionPeriod(newPeriod)
                }
                setPackageUpgradeModalOpen(false)
                setPackageUpgradeItem(null)
                queryClient.invalidateQueries(['subscriptionsSummary'])
                queryClient.invalidateQueries(['subscriptionsRetentionPeriod'])
                queryClient.invalidateQueries(['unitSettings'])
                queryClient.invalidateQueries(['unitSettingsUsage'])
            },
            onError: (error) => {
                const msg =
                    error?.response?.data?.Message ||
                    error?.response?.data?.message ||
                    error?.message
                if (msg) {
                    AlertToastr.showErrorAlert(msg)
                }
            },
        },
    )

    const requestSort = (key) => {
        let direction = ascConst
        if (sortConfig && sortConfig.key === key && sortConfig.direction === ascConst) {
            direction = descConst
        }
        const next = { key, direction }
        unitSettingsSortRef.current = next
        setSortConfig(next)
        setPageNumber(1)
    }

    const getClassNamesFor = (name) => {
        if (!sortConfig || sortConfig.key !== name) {
            return
        }
        if (sortConfig.direction) {
            return sortConfig.direction === ascConst ? arrowDownClassName : arrowUpClassName
        }
    }

    const onPrevPage = useCallback(() => {
        setPageNumber(p => Math.max(1, p - 1))
        window.scrollTo(0, 0)
    }, [])

    const onNextPage = useCallback(() => {
        setPageNumber(p => Math.min(pageCount || 1, p + 1))
        window.scrollTo(0, 0)
    }, [pageCount])

    const handleRowCount = useCallback((count) => {
        setPageSize(count)
        setPageNumber(1)
    }, [])

    const onToggleEnabled = (row) => {
        if (togglingUnitUid === row.unitUId) {
            return
        }
        setTogglingUnitUid(row.unitUId)
        patchMutation.mutate({ unitUid: row.unitUId, isEnabled: !row.isEnabled })
    }

    const isLoading =
        tableView === TABLE_VIEW_ACTIVITY
            ? activityQuery.isLoading || activityQuery.isFetching
            : allUnitsQuery.isLoading || allUnitsQuery.isFetching
    const subscriptionsSummary = subscriptionsSummaryQuery.data?.data ?? {}
    const currentPackage = subscriptionsSummary.currentPackage ?? {}
    const retentionPeriodFromSummary = subscriptionsSummary.retentionPeriod ?? null
    const retentionPeriod =
        subscriptionsRetentionPeriodQuery.data?.data ?? retentionPeriodFromSummary
    const retentionOptions = useMemo(() => {
        const options = subscriptionsRetentionOptionsQuery.data?.data
        return Array.isArray(options) ? options : []
    }, [subscriptionsRetentionOptionsQuery.data])
    const enabledDevices = Number(subscriptionsSummary.billableDevices) || 0

    const subscriptionPackages = useMemo(() => {
        const rawPackages = Array.isArray(subscriptionsSummary.packages)
            ? subscriptionsSummary.packages
            : []
        return [...rawPackages]
            .map((pkg) => ({
                ...pkg,
                allowedDevices: Number(pkg.allowedDevices) || 0,
            }))
            .sort((a, b) => a.allowedDevices - b.allowedDevices)
    }, [subscriptionsSummary.packages])

    const currentPackageAllowedDevices = Number(currentPackage.allowedDevices) || 0
    const currentPackageName = currentPackage.name || 'Subscription'
    const currentPackageIsFree =
        currentPackage.isFree === true || currentPackage.IsFree === true
    const currentPackageIndex = useMemo(
        () =>
            subscriptionPackages.findIndex(
                (pkg) =>
                    pkg.name === currentPackage.name &&
                    pkg.allowedDevices === currentPackageAllowedDevices,
            ),
        [subscriptionPackages, currentPackage.name, currentPackageAllowedDevices],
    )
    const nextPackage =
        currentPackageIndex >= 0 &&
        currentPackageIndex < subscriptionPackages.length - 1
            ? subscriptionPackages[currentPackageIndex + 1]
            : null
    const nextPackageFromSummary = subscriptionsSummary.nextPackage ?? null
    const nextPackageAllowedDevices = Number(nextPackageFromSummary?.allowedDevices) > 0
        ? Number(nextPackageFromSummary.allowedDevices)
        : Number(nextPackage?.allowedDevices) || 0
    const lastPackageAllowedDevices =
        subscriptionPackages.length > 0
            ? subscriptionPackages[subscriptionPackages.length - 1].allowedDevices
            : Math.max(currentPackageAllowedDevices, 1)
    const scaleEnabledDevices = Math.min(enabledDevices, lastPackageAllowedDevices)
    const allTiersMaxDevices = Math.max(
        ...subscriptionPackages.map((pkg) => pkg.allowedDevices),
        currentPackageAllowedDevices,
        1,
    )
    // Segments always match the current package cap (e.g. Cloud 5 -> 5 stripes only).
    const nextTierSegmentCount = Math.min(
        Math.max(currentPackageAllowedDevices, enabledDevices, 1),
        lastPackageAllowedDevices,
    )
    const activeUnitsLabel = `${enabledDevices} OF ${Math.max(currentPackageAllowedDevices, 1)} UNITS ACTIVE`
    const retentionMenuOptions = useMemo(() => {
        const normalized = retentionOptions.map((option) => {
            const value = getRetentionPeriodValue(option)
            const label = option?.name ?? option?.Name ?? String(value ?? '')
            return value == null ? null : { value: String(value), label }
        }).filter(Boolean)
        if (normalized.length > 0) return normalized

        const fallbackValue = getRetentionPeriodValue(retentionPeriod)
        const fallbackLabel = retentionPeriod?.name ?? retentionPeriod?.Name
        if (fallbackValue == null) return []
        return [{ value: String(fallbackValue), label: fallbackLabel ?? String(fallbackValue) }]
    }, [retentionOptions, retentionPeriod, getRetentionPeriodValue])

    const selectedSummaryRetentionPeriodValue =
        summaryDataRetentionPeriodParam != null ? String(summaryDataRetentionPeriodParam) : ''
    const selectedActivityRetentionPeriodValue =
        activityDataRetentionPeriodParam != null ? String(activityDataRetentionPeriodParam) : ''

    const closePackageUpgradeModal = useCallback(() => {
        pendingRetentionPeriodRef.current = null
        const prev = retentionPeriodBeforePendingChangeRef.current
        if (prev != null) {
            setSelectedSummaryRetentionPeriod(prev)
            retentionPeriodBeforePendingChangeRef.current = null
        }
        setPackageUpgradeModalOpen(false)
        setPackageUpgradeItem(null)
        queryClient.invalidateQueries(['subscriptionsRetentionPeriod'])
        queryClient.invalidateQueries(['subscriptionsSummary'])
    }, [queryClient])

    const onPackageUpgradeConfirm = useCallback(() => {
        const code =
            packageUpgradeItem?.confirmationCode ?? packageUpgradeItem?.ConfirmationCode
        if (!code) return
        confirmPackageUpgradeMutation.mutate({ code })
    }, [confirmPackageUpgradeMutation, packageUpgradeItem])

    const onRetentionPeriodChange = useCallback(
        (event) => {
            const nextValue = event.target.value
            if (nextValue == null || nextValue === '') return
            if (String(nextValue) === selectedSummaryRetentionPeriodValue) return
            retentionPeriodBeforePendingChangeRef.current =
                selectedSummaryRetentionPeriodValue === ''
                    ? null
                    : selectedSummaryRetentionPeriodValue
            patchRetentionPeriodMutation.mutate({ dataRetentionPeriod: nextValue })
        },
        [patchRetentionPeriodMutation, selectedSummaryRetentionPeriodValue],
    )

    const onActivityRetentionPeriodChange = useCallback((nextValue) => {
        if (nextValue == null || nextValue === '') return
        if (String(nextValue) === selectedActivityRetentionPeriodValue) return
        setSelectedActivityRetentionPeriod(nextValue)
        // Reset pagination/sort when changing Activity period; search filter is kept.
        setPageNumber(1)
        setPageSize(15)
        setSortConfig(ACTIVITY_DEFAULT_SORT)
    }, [selectedActivityRetentionPeriodValue])

    const onTableViewChange = useCallback(
        (nextView) => {
            if (nextView === tableView) return
            setTableView(nextView)
            setPageNumber(1)
            setPageSize(15)
            if (nextView === TABLE_VIEW_ACTIVITY) {
                const sharedSortKeys = ['uId', 'name']
                setSortConfig(
                    sharedSortKeys.includes(sortConfig?.key)
                        ? sortConfig
                        : ACTIVITY_DEFAULT_SORT,
                )
                setSelectedActivityRetentionPeriod(
                    selectedSummaryRetentionPeriodValue !== ''
                        ? selectedSummaryRetentionPeriodValue
                        : null,
                )
            } else {
                setSortConfig(unitSettingsSortRef.current)
            }
        },
        [tableView, selectedSummaryRetentionPeriodValue, sortConfig],
    )

    const nextTierLabelThresholds = useMemo(() => {
        const showNextPackageEndLabel =
            currentPackageAllowedDevices <= 5 &&
            nextPackageAllowedDevices > currentPackageAllowedDevices
        const extraThresholds =
            showNextPackageEndLabel &&
            nextPackageAllowedDevices <= nextTierSegmentCount
                ? [nextPackageAllowedDevices]
                : []
        return getScaleLabelThresholds({
            scaleMax: nextTierSegmentCount,
            currentPackageAllowedDevices,
            extraThresholds,
        })
    }, [
        nextTierSegmentCount,
        currentPackageAllowedDevices,
        nextPackageAllowedDevices,
    ])

    const nextTierSeparatorThresholds = useMemo(
        () =>
            new Set(
                nextTierLabelThresholds.filter(
                    (threshold) => threshold < nextTierSegmentCount,
                ),
            ),
        [nextTierLabelThresholds, nextTierSegmentCount],
    )

    const nextTierTrackItems = useMemo(() => {
        const currentMax = Math.max(nextTierSegmentCount, 1)
        const items = []

        for (let index = 0; index < currentMax; index += 1) {
            const unitNumber = index + 1
            const isFilled = unitNumber <= scaleEnabledDevices
            const isCurrentTierRange = unitNumber <= 5
            let className = 'subscriptionSegment'
            if (!isFilled) className += ' empty'
            else if (isCurrentTierRange) className += ' current'
            else className += ' next'
            items.push({
                key: `segment-${index}`,
                className,
            })
            if (
                nextTierSeparatorThresholds.has(unitNumber) &&
                unitNumber < currentMax
            ) {
                items.push({
                    key: `tier-divider-${unitNumber}`,
                    className: 'subscriptionTierDividerSlot',
                })
            }
        }
        return items
    }, [scaleEnabledDevices, nextTierSegmentCount, nextTierSeparatorThresholds])

    const nextTierLabels = useMemo(
        () =>
            buildScaleLabelItems(
                nextTierLabelThresholds,
                subscriptionPackages,
                nextTierSegmentCount,
            ),
        [nextTierLabelThresholds, subscriptionPackages, nextTierSegmentCount],
    )

    const allTierLabelThresholds = useMemo(
        () =>
            getScaleLabelThresholds({
                scaleMax: allTiersMaxDevices,
                currentPackageAllowedDevices,
            }),
        [allTiersMaxDevices, currentPackageAllowedDevices],
    )

    const allTierSeparatorThresholds = useMemo(
        () =>
            new Set(
                allTierLabelThresholds.filter(
                    (threshold) => threshold < allTiersMaxDevices,
                ),
            ),
        [allTierLabelThresholds, allTiersMaxDevices],
    )

    const allTierSegments = useMemo(() => {
        const max = Math.max(allTiersMaxDevices, 1)
        // In "All tiers", first 5 segments are green, starting from the 6th use primary.
        const firstTierSize = Math.max(Number(subscriptionPackages?.[0]?.allowedDevices) || 5, 1)
        const primaryStartThreshold = firstTierSize + 1
        const items = []

        for (let index = 0; index < max; index += 1) {
            const unitNumber = index + 1
            let className = 'subscriptionSegment'
            if (unitNumber > scaleEnabledDevices) {
                className += ' empty'
            } else if (unitNumber < primaryStartThreshold) {
                className += ' current'
            } else {
                className += ' next'
            }
            items.push({ key: `tiny-segment-${index}`, className })
            if (
                allTierSeparatorThresholds.has(unitNumber) &&
                unitNumber < max
            ) {
                items.push({
                    key: `tier-divider-${unitNumber}`,
                    className: 'subscriptionTierDividerSlot',
                })
            }
        }
        return items
    }, [
        scaleEnabledDevices,
        allTiersMaxDevices,
        subscriptionPackages,
        allTierSeparatorThresholds,
    ])

    const allTierLabels = useMemo(
        () =>
            buildScaleLabelItems(
                allTierLabelThresholds,
                subscriptionPackages,
                allTiersMaxDevices,
            ),
        [allTierLabelThresholds, subscriptionPackages, allTiersMaxDevices],
    )

    return (
        <>
            <RetentionPeriodConfirmModal
                open={packageUpgradeModalOpen}
                upgradeItem={packageUpgradeItem}
                onClose={closePackageUpgradeModal}
                onConfirm={onPackageUpgradeConfirm}
                confirmLoading={confirmPackageUpgradeMutation.isLoading}
            />
            <div className="pageCard subscriptionCard">
                <div className="subscriptionCardHeader">
                    <div className="subscriptionCardTitleGroup">
                        <div className="subscriptionCardTitle">{currentPackageName}</div>
                        {currentPackageIsFree && (
                            <span className="subscriptionFreeBadge">{translation.unitSettingsFreeBadge}</span>
                        )}
                    </div>
                </div>
                <div className="subscriptionCardBody">
                    <div className="subscriptionTierModes">
                        <button
                            type="button"
                            className={`subscriptionTierModeButton ${subscriptionMode === NEXT_TIER_MODE ? 'active' : ''}`}
                            onClick={() => setSubscriptionMode(NEXT_TIER_MODE)}
                        >
                            {translation.unitSettingsCurrentTierButton}
                        </button>
                        <button
                            type="button"
                            className={`subscriptionTierModeButton ${subscriptionMode === ALL_TIERS_MODE ? 'active' : ''}`}
                            onClick={() => setSubscriptionMode(ALL_TIERS_MODE)}
                        >
                            {translation.unitSettingsAllTiersButton}
                        </button>
                    </div>
                    <div className="subscriptionRetentionControl">
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            label={translation.unitSettingsDataRetentionPeriodLabel}
                            className="subscriptionRetentionSelect"
                            value={selectedSummaryRetentionPeriodValue}
                            onChange={onRetentionPeriodChange}
                            disabled={
                                subscriptionsRetentionOptionsQuery.isLoading ||
                                patchRetentionPeriodMutation.isLoading ||
                                packageUpgradeModalOpen ||
                                confirmPackageUpgradeMutation.isLoading
                            }
                        >
                            {retentionMenuOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                    <div className="subscriptionUnitsActiveLabel">{activeUnitsLabel}</div>
                    {subscriptionMode === NEXT_TIER_MODE ? (
                        <div className="subscriptionTierScrollWrap">
                            <div className="subscriptionTierScrollInner">
                                {renderTierProgressTrack(nextTierTrackItems)}
                                <div className="subscriptionTierMarkers">
                                    {nextTierLabels.map((label) => (
                                        <span
                                            key={label.key}
                                            style={{
                                                left: label.isFirst
                                                    ? 0
                                                    : label.isLast
                                                        ? '100%'
                                                        : `${Math.max(0, Math.min(label.leftPercent, 100))}%`,
                                                transform: label.isFirst
                                                    ? 'none'
                                                    : label.isLast
                                                        ? 'translateX(-100%)'
                                                        : 'translateX(-50%)',
                                            }}
                                        >
                                            {label.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="subscriptionTierScrollWrap">
                            <div className="subscriptionTierScrollInner">
                                {renderTierProgressTrack(allTierSegments)}
                                <div className="subscriptionTierMarkers">
                                    {allTierLabels.map((label) => (
                                        <span
                                            key={label.key}
                                            style={{
                                                left: label.isFirst
                                                    ? 0
                                                    : label.isLast
                                                        ? '100%'
                                                        : `${Math.max(0, Math.min(label.leftPercent, 100))}%`,
                                                transform: label.isFirst
                                                    ? 'none'
                                                    : label.isLast
                                                        ? 'translateX(-100%)'
                                                        : 'translateX(-50%)',
                                            }}
                                        >
                                            {label.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="pageCard subscriptionCard">
                <div className="pageBtnSection pt0 mt5 unitSettingsPageToolbar">
                    <div className="searchHolder">
                        <div className="inputHolder" style={{ display: 'flex', verticalAlign: 'center' }}>
                            <SearchInput search={searchFilter} onChange={setSearchFilter} />
                        </div>
                    </div>
                    <div className="subscriptionTierModes">
                        <button
                            type="button"
                            className={`subscriptionTierModeButton ${tableView === TABLE_VIEW_ALL_UNITS ? 'active' : ''}`}
                            onClick={() => onTableViewChange(TABLE_VIEW_ALL_UNITS)}
                        >
                            {translation.unitSettingsUnitSettingsTabButton}
                        </button>
                        <button
                            type="button"
                            className={`subscriptionTierModeButton ${tableView === TABLE_VIEW_ACTIVITY ? 'active' : ''}`}
                            onClick={() => onTableViewChange(TABLE_VIEW_ACTIVITY)}
                        >
                            {translation.unitSettingsActivityTabButton}
                        </button>
                    </div>
                </div>
                <div className="tableHolder" style={{ position: 'relative', minHeight: 120 }}>
                    {tableView === TABLE_VIEW_ACTIVITY ? (
                        <div className="activityUsageTable">
                            <div className="activityUsageTopRow">
                                <div className="activityUsageLegend">
                                    <span className="activityLegendItem">
                                        <span className="activityLegendDot currentlyActive" />
                                        {translation.unitSettingsActivityLegendCurrentlyActive}
                                    </span>
                                    <span className="activityLegendItem">
                                        <span className="activityLegendDot activePeriod" />
                                        {translation.unitSettingsActivityLegendPreviousPeriod}
                                    </span>
                                    <span className="activityLegendItem">
                                        <span className="activityLegendDot notBilled" />
                                        {translation.unitSettingsActivityLegendNotBilled}
                                    </span>
                                    <span className="activityLegendItem">
                                        <span className="activityLegendDot notBilledPrevious" />
                                        {translation.unitSettingsActivityLegendNotBilledPrevious}
                                    </span>
                                    {isSuperUser && (
                                        <span className="activityLegendItem">
                                            <span className="activityLegendDot excludedFromBilling" />
                                            {translation.unitSettingsActivityLegendExcludedFromBilling}
                                        </span>
                                    )}
                                </div>
                                {retentionMenuOptions.length > 0 && (
                                    <>
                                        <div className="activityPeriodBlock">
                                            <span className="activityPeriodLabel">{translation.unitSettingsActivityPeriodFilterLabel}</span>
                                            <div className="activityPeriodOptions">
                                                {retentionMenuOptions.map((option) => {
                                                    const isActive =
                                                        option.value ===
                                                        selectedActivityRetentionPeriodValue
                                                    return (
                                                        <button
                                                            key={option.value}
                                                            type="button"
                                                            className={`activityPeriodOption ${isActive ? 'active' : ''}`}
                                                            onClick={() =>
                                                                onActivityRetentionPeriodChange(
                                                                    option.value,
                                                                )
                                                            }
                                                        >
                                                            {option.label}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                        <div className="activityUsageBillableCol">
                                            {activityBillableUnitsCount != null && (
                                                <span className="activityPeriodLabel activityBillableUnits">
                                                    {`${translation.unitSettingsActivityBillableUnitsPrefix}: ${activityBillableUnitsCount}`}
                                                </span>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="activityUsageRows">
                                {items.map((row) => (
                                    <CursorTooltip
                                        key={row.unitUId}
                                        placement="top"
                                        title={row.isDeleted ? translation.unitSettingsDeletedTooltip : ''}
                                    >
                                        <div className={`activityUsageRow${row.isDeleted ? ' unitRowDeleted' : ''}`}>
                                            <div className="activityUsageUnitCell">
                                                <div className="unitName">{row.unitName}</div>
                                                <div className="unitUid">{row.unitUId}</div>
                                            </div>
                                            <div className="activityUsageTimelineCell">
                                                <div className="activityUsageTrack">
                                                    {(row.activityMarkers || []).map((marker) => {
                                                        const activityMarkerTipId = `${row.unitUId}\u0001${marker.key}`
                                                        if (marker.tooltipData.variant === 'interval') {
                                                            const isTipOpen =
                                                                activeActivityMarkerTooltipId === activityMarkerTipId
                                                            return (
                                                                <Tooltip
                                                                    key={`${row.unitUId}-${marker.key}`}
                                                                    arrow
                                                                    placement="top"
                                                                    disableHoverListener
                                                                    disableFocusListener
                                                                    disableTouchListener
                                                                    open={isTipOpen}
                                                                    onClose={() => setActiveActivityMarkerTooltipId(null)}
                                                                    PopperProps={{
                                                                        style: { pointerEvents: 'auto' },
                                                                    }}
                                                                    classes={{
                                                                        tooltip: 'activityUsageTooltip',
                                                                        arrow: 'activityUsageTooltipArrow',
                                                                    }}
                                                                    title={(
                                                                        <div
                                                                            className="activityUsageTooltipContent"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                        >
                                                                            <div className="activityUsageTooltipTitle">
                                                                                {marker.tooltipData.excludeFromBilling
                                                                                    ? translation.unitSettingsActivityLegendExcludedFromBilling.toUpperCase()
                                                                                    : marker.isCurrent
                                                                                        ? translation.unitSettingsActivityTooltipCurrentlyActive
                                                                                        : translation.unitSettingsActivityTooltipPreviousPeriod}
                                                                            </div>
                                                                            {!row.isBillable && !marker.tooltipData.excludeFromBilling && (
                                                                                <div className="activityUsageTooltipMeta">
                                                                                    {translation.unitSettingsActivityLegendNotBilled}
                                                                                </div>
                                                                            )}
                                                                            <div className="activityUsageTooltipRange">
                                                                                {`${formatDateTimeLong(marker.tooltipData.start)} \u2192 ${marker.tooltipData.end ? formatDateTimeLong(marker.tooltipData.end) : translation.unitSettingsActivityTooltipNow}`}
                                                                            </div>
                                                                            <div className="activityUsageTooltipDuration">
                                                                                {`${translation.unitSettingsActivityTooltipDurationPrefix}: ${marker.tooltipData.duration}`}
                                                                            </div>
                                                                            <div className="activityUsageTooltipDivider" />
                                                                            <div className="activityUsageTooltipMeta">{`${translation.unitSettingsActivityTooltipActivatedByPrefix}: ${marker.tooltipData.activatedByDisplay || formatActor(marker.tooltipData.activatedBy)}`}</div>
                                                                            <div className="activityUsageTooltipMeta">{`${translation.unitSettingsActivityTooltipDeactivatedByPrefix}: ${marker.tooltipData.deactivatedByDisplay || formatActor(marker.tooltipData.deactivatedBy)}`}</div>
                                                                            {isSuperUser && (
                                                                                <div
                                                                                    className="activityUsageTooltipExcludeSwitchRow"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                >
                                                                                    <span className="activityUsageTooltipExcludeSwitchLabel">
                                                                                        {translation.unitSettingsActivityTooltipExcludeFromBilling}
                                                                                    </span>
                                                                                    <Switch
                                                                                        color="primary"
                                                                                        size="small"
                                                                                        checked={Boolean(
                                                                                            marker.tooltipData.excludeFromBilling,
                                                                                        )}
                                                                                        disabled={patchUsageIntervalMutation.isLoading}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        onChange={(_, checked) => {
                                                                                            patchUsageIntervalMutation.mutate({
                                                                                                body: buildUsageIntervalPatchBody(
                                                                                                    row,
                                                                                                    marker,
                                                                                                    checked,
                                                                                                ),
                                                                                                config: companyConfig,
                                                                                            })
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                >
                                                                    <span
                                                                        role="presentation"
                                                                        className={`activityUsageMarker ${
                                                                            marker.excludeFromBilling
                                                                                ? `excluded${marker.isCurrent ? ' current' : ''}`
                                                                                : (marker.isCurrent ? 'current' : 'active')
                                                                        }${!row.isBillable && !marker.excludeFromBilling ? ' notBilled' : ''}${isTipOpen ? ' activityUsageMarker--open' : ''}`}
                                                                        style={{
                                                                            left: `${Math.max(0, Math.min(marker.left, 100))}%`,
                                                                            width: `${Math.max(0, Math.min(marker.width, 100 - Math.max(0, marker.left)))}%`,
                                                                        }}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            setActiveActivityMarkerTooltipId(activityMarkerTipId)
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            )
                                                        }
                                                        const isLogTipOpen =
                                                            activeActivityMarkerTooltipId === activityMarkerTipId
                                                        return (
                                                            <Tooltip
                                                                key={`${row.unitUId}-${marker.key}`}
                                                                arrow
                                                                placement="top"
                                                                disableHoverListener
                                                                disableFocusListener
                                                                disableTouchListener
                                                                open={isLogTipOpen}
                                                                onClose={() => setActiveActivityMarkerTooltipId(null)}
                                                                classes={{
                                                                    tooltip: 'activityUsageTooltip',
                                                                    arrow: 'activityUsageTooltipArrow',
                                                                }}
                                                                title={(
                                                                    <div
                                                                        className="activityUsageTooltipContent"
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                    >
                                                                        <div className="activityUsageTooltipTitle">
                                                                            {marker.isCurrent
                                                                                ? translation.unitSettingsActivityTooltipCurrentlyActive
                                                                                : translation.unitSettingsActivityTooltipPreviousPeriod}
                                                                        </div>
                                                                        {!row.isBillable && (
                                                                            <div className="activityUsageTooltipMeta">
                                                                                {translation.unitSettingsActivityLegendNotBilled}
                                                                            </div>
                                                                        )}
                                                                        <div className="activityUsageTooltipRange">
                                                                            {`${formatDateTimeLong(marker.tooltipData.start)} \u2192 ${marker.tooltipData.end ? formatDateTimeLong(marker.tooltipData.end) : translation.unitSettingsActivityTooltipNow}`}
                                                                        </div>
                                                                        <div className="activityUsageTooltipDuration">{`${translation.unitSettingsActivityTooltipDurationPrefix}: ${marker.tooltipData.duration}`}</div>
                                                                        <div className="activityUsageTooltipDivider" />
                                                                        <div className="activityUsageTooltipMeta">{`${translation.unitSettingsActivityTooltipActivatedByPrefix}: ${marker.tooltipData.activatedByDisplay || formatActor(marker.tooltipData.activatedBy)}`}</div>
                                                                        <div className="activityUsageTooltipMeta">{`${translation.unitSettingsActivityTooltipDeactivatedByPrefix}: ${marker.tooltipData.deactivatedByDisplay || formatActor(marker.tooltipData.deactivatedBy)}`}</div>
                                                                    </div>
                                                                )}
                                                            >
                                                                <span
                                                                    role="presentation"
                                                                    className={`activityUsageMarker ${
                                                                        marker.excludeFromBilling
                                                                            ? `excluded${marker.isCurrent ? ' current' : ''}`
                                                                            : (marker.isCurrent ? 'current' : 'active')
                                                                    }${!row.isBillable && !marker.excludeFromBilling ? ' notBilled' : ''}${isLogTipOpen ? ' activityUsageMarker--open' : ''}`}
                                                                    style={{
                                                                        left: `${Math.max(0, Math.min(marker.left, 100))}%`,
                                                                        width: `${Math.max(0, Math.min(marker.width, 100 - Math.max(0, marker.left)))}%`,
                                                                    }}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        setActiveActivityMarkerTooltipId(activityMarkerTipId)
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                            <div className="activityUsageTotalCell">
                                                {row.totalEnabledTime}
                                            </div>
                                        </div>
                                    </CursorTooltip>
                                ))}
                            </div>
                            <div className="activityUsageAxis">
                                <div className="activityUsageAxisLeft" />
                                <div
                                    className="activityUsageAxisMonths"
                                    style={{
                                        gridTemplateColumns: `repeat(${Math.max(activityTimeAxis.labels.length, 1)}, minmax(0, 1fr))`,
                                    }}
                                >
                                    {activityTimeAxis.labels.map((item) => (
                                        <div className="activityUsageAxisMonthBlock" key={item.key}>
                                            {shouldShowDetailedActivityScale && (
                                                <div className="activityUsageScaleTicks">
                                                    {buildMonthDayTicks(item.monthDate).map((day) => (
                                                        <span key={`${item.key}-d-${day}`} aria-hidden="true" />
                                                    ))}
                                                </div>
                                            )}
                                            <span className="activityUsageMonthLabel">{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="activityUsageAxisRight" />
                            </div>
                        </div>
                    ) : (
                        <table className="unitSettingsTable">
                            <thead>
                                <tr className="headingRow">
                                    <th
                                        onClick={() => requestSort('uId')}
                                        className={getClassNamesFor('uId')}
                                    >
                                        {translation.settingsUnitSettingsUIdHeader}
                                    </th>
                                    <th
                                        onClick={() => requestSort('name')}
                                        className={getClassNamesFor('name')}
                                        style={{ width: '18%' }}
                                    >
                                        {translation.settingsUnitSettingsNameHeader}
                                    </th>
                                    <th
                                        style={{ width: '16%' }}
                                    >
                                        {translation.unitSettingsChangedByHeader}
                                    </th>
                                    <th
                                        style={{ width: '18%' }}
                                    >
                                        {translation.unitSettingsLastChangedHeader}
                                    </th>
                                    <th
                                        style={{ width: '16%' }}
                                    >
                                        {translation.unitSettingsAddedOnHeader}
                                    </th>
                                    <th
                                        onClick={() => requestSort('status')}
                                        className={`text-left ${getClassNamesFor('status')}`}
                                        style={{ width: '90px' }}
                                    >
                                        {translation.unitSettingsSubscriptionStatusHeader}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(row => (
                                    <CursorTooltip
                                        key={row.unitUId}
                                        placement="top"
                                        title={row.isDeleted ? translation.unitSettingsDeletedTooltip : ''}
                                    >
                                        <tr className={row.isDeleted ? 'unitRowDeleted' : undefined}>
                                            <td>{row.unitUId}</td>
                                            <td>{row.unitName}</td>
                                            <td>{row.changedBy}</td>
                                            <td>{row.lastChanged}</td>
                                            <td>{row.addedOn}</td>
                                            <td className="text-left switchCell">
                                                {row.isDeleted ? (
                                                    <Switch checked={false} color="primary" disabled />
                                                ) : (
                                                    <Switch
                                                        checked={!!row.isEnabled}
                                                        color="primary"
                                                        onChange={() => onToggleEnabled(row)}
                                                        disabled={togglingUnitUid === row.unitUId}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    </CursorTooltip>
                                ))}
                            </tbody>
                        </table>
                    )}
                    <div className={classes.root}>
                        <Fade in={isLoading} unmountOnExit>
                            <CircularProgress />
                        </Fade>
                    </div>
                </div>
                {pagination.itemsTotalCount > 0 && (
                    <TablePagination
                        pagination={pagination}
                        onPrevPage={onPrevPage}
                        onNextPage={onNextPage}
                        handleRowCount={handleRowCount}
                    />
                )}
            </div>
        </>
    )
}

export default UnitSettings
